/** Genesis pool address — linear vesting status + claim via connected DGC Wallet. */

import { useCurrentAccount, useSuiClient } from '@mysten/dapp-kit';
import { buildClaimVestedToBalance } from '@cpc/tx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

import { CpcConnectButton } from '~/components/cpc/CpcConnectButton';
import { useCpcSignAndExecuteTransaction } from '~/hooks/useCpcSignAndExecuteTransaction';
import {
	usePoolVestingWallets,
	type PoolVestingWalletView,
} from '~/hooks/usePoolVestingWallets';
import { tryNormalizeToHexAddress } from '~/utils/cpcAddress';
import { CPC_SYMBOL } from '~/utils/cpcCoin';
import { extractMoveCallsFromTxBlock } from '~/utils/cpcMoveCallMetrics';

function formatMist(mist: bigint): string {
	const whole = mist / 1_000_000_000n;
	const frac = mist % 1_000_000_000n;
	if (frac === 0n) return whole.toLocaleString('zh-CN');
	const fracStr = frac.toString().padStart(9, '0').replace(/0+$/, '');
	return `${whole.toLocaleString('zh-CN')}.${fracStr}`;
}

function formatTime(ms: number): string {
	try {
		return new Date(ms).toLocaleString('zh-CN');
	} catch {
		return String(ms);
	}
}

function sameAddress(a: string, b: string): boolean {
	try {
		return tryNormalizeToHexAddress(a).toLowerCase() === tryNormalizeToHexAddress(b).toLowerCase();
	} catch {
		return a.trim().toLowerCase() === b.trim().toLowerCase();
	}
}

function WalletCard({
	wallet,
	packageId,
	poolAddress,
	onClaimed,
}: {
	wallet: PoolVestingWalletView;
	packageId: string;
	poolAddress: string;
	onClaimed: () => void;
}) {
	const account = useCurrentAccount();
	const { mutateAsync: signAndExecute, isPending } = useCpcSignAndExecuteTransaction();
	const [busy, setBusy] = useState(false);

	const isOwner = Boolean(account?.address && sameAddress(account.address, poolAddress));
	const canClaim = wallet.claimableMist > 0n && !wallet.notStarted;

	const onClaim = useCallback(async () => {
		if (!isOwner) {
			toast.error('请用该池地址对应的 DGC Wallet 账户连接后再领取');
			return;
		}
		if (!canClaim) {
			toast.error('当前无可领额度');
			return;
		}
		setBusy(true);
		try {
			const tx = buildClaimVestedToBalance({
				packageId,
				walletId: wallet.objectId,
			});
			const result = await signAndExecute({ transaction: tx });
			toast.success(`领取成功${result.digest ? ` · ${result.digest.slice(0, 10)}…` : ''}`);
			onClaimed();
		} catch (e) {
			console.error(e);
			toast.error(e instanceof Error ? e.message : '领取失败');
		} finally {
			setBusy(false);
		}
	}, [canClaim, isOwner, onClaimed, packageId, signAndExecute, wallet.objectId]);

	return (
		<div className="rounded-lg border border-white/10 bg-black/25 px-3 py-3">
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div>
					<p className="text-[11px] text-[#64748b]">Wallet</p>
					<Link
						to={`/object/${wallet.objectId}`}
						className="font-mono text-xs text-[#7dd3fc] hover:underline"
					>
						{wallet.objectId.slice(0, 10)}…{wallet.objectId.slice(-8)}
					</Link>
				</div>
				<span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-[#cbd5e1]">
					{wallet.notStarted ? '未开始' : wallet.ended ? '已结束' : '释放中'}
				</span>
			</div>

			<div className="mt-3">
				<div className="mb-1 flex justify-between text-[11px] text-[#94a3b8]">
					<span>进度 {wallet.progressPct.toFixed(2)}%</span>
					<span>
						已领 {formatMist(wallet.claimedMist)} / 总量 {formatMist(wallet.totalMist)}{' '}
						{CPC_SYMBOL}
					</span>
				</div>
				<div className="h-2 overflow-hidden rounded-full bg-white/10">
					<div
						className="h-full rounded-full bg-[#38BDF8]"
						style={{ width: `${Math.max(wallet.progressPct, wallet.progressPct > 0 ? 0.5 : 0)}%` }}
					/>
				</div>
			</div>

			<div className="mt-3 grid gap-2 sm:grid-cols-2">
				<div>
					<p className="text-[11px] text-[#64748b]">当前可领</p>
					<p className="text-lg font-semibold tabular-nums text-white">
						{formatMist(wallet.claimableMist)}{' '}
						<span className="text-sm font-medium text-[#aeb9d0]">{CPC_SYMBOL}</span>
					</p>
				</div>
				<div>
					<p className="text-[11px] text-[#64748b]">仍锁在 Wallet</p>
					<p className="text-base font-semibold tabular-nums text-[#e2e8f0]">
						{formatMist(wallet.remainingMist)} {CPC_SYMBOL}
					</p>
				</div>
			</div>

			<p className="mt-2 text-[11px] leading-relaxed text-[#64748b]">
				开始 {formatTime(wallet.startMs)} · 结束{' '}
				{formatTime(wallet.startMs + wallet.durationMs)} · 连续线性、无 cliff
			</p>

			<div className="mt-3 flex flex-wrap items-center gap-2">
				{!account ? (
					<div className="scale-90 origin-left">
						<CpcConnectButton />
					</div>
				) : null}
				<button
					type="button"
					disabled={!canClaim || busy || isPending || (Boolean(account) && !isOwner)}
					onClick={onClaim}
					className="rounded-lg bg-[#38BDF8] px-3 py-1.5 text-sm font-semibold text-[#0f172a] disabled:cursor-not-allowed disabled:opacity-40"
				>
					{busy || isPending ? '领取中…' : '领取可领额度'}
				</button>
				{account && !isOwner ? (
					<p className="text-[11px] text-amber-300/90">
						已连接 {account.address.slice(0, 8)}…，需切换为池地址账户才能领取
					</p>
				) : null}
			</div>
		</div>
	);
}

function ClaimHistory({ owner, packageId }: { owner: string; packageId: string }) {
	const client = useSuiClient();
	const { data, isPending } = useQuery({
		queryKey: ['pool-vesting-claims', owner, packageId],
		enabled: Boolean(owner && packageId),
		refetchInterval: 60_000,
		queryFn: async () => {
			const res = await client.queryTransactionBlocks({
				filter: { FromAddress: owner },
				order: 'descending',
				limit: 40,
				options: { showInput: true, showEffects: true },
			});
			const rows: { digest: string; ts: string | null; fn: string }[] = [];
			for (const tx of res.data ?? []) {
				const { calls } = extractMoveCallsFromTxBlock(tx);
				for (const call of calls) {
					const fn = call.function;
					const mod = call.module;
					if (
						(mod === 'cpc_setup' || mod === 'setup') &&
						(fn === 'claim_vested_to_balance' ||
							fn === 'claim_vested_as_coin' ||
							fn === 'claim_vested')
					) {
						rows.push({
							digest: tx.digest,
							ts: tx.timestampMs ?? null,
							fn,
						});
						break;
					}
				}
			}
			return rows.slice(0, 8);
		},
	});

	if (isPending) {
		return <p className="text-xs text-[#64748b]">正在加载领取记录…</p>;
	}
	if (!data?.length) {
		return <p className="text-xs text-[#64748b]">暂无领取交易记录（或尚未发起过 claim）</p>;
	}

	return (
		<ul className="space-y-1.5">
			{data.map((row) => (
				<li key={row.digest} className="flex flex-wrap items-center gap-2 text-xs">
					<Link to={`/txblock/${row.digest}`} className="font-mono text-[#7dd3fc] hover:underline">
						{row.digest.slice(0, 12)}…
					</Link>
					<span className="text-[#64748b]">{row.fn}</span>
					{row.ts ? (
						<span className="text-[#64748b]">{formatTime(Number(row.ts))}</span>
					) : null}
				</li>
			))}
		</ul>
	);
}

export function CpcPoolVestingPanel({
	address,
	poolName,
}: {
	address: string;
	poolName: string;
}) {
	const queryClient = useQueryClient();
	const { packageId, pkgLoading, wallets, isPending, isError, refetch } = usePoolVestingWallets(
		address,
		true,
	);

	const totals = useMemo(() => {
		let claimable = 0n;
		let claimed = 0n;
		let remaining = 0n;
		for (const w of wallets) {
			claimable += w.claimableMist;
			claimed += w.claimedMist;
			remaining += w.remainingMist;
		}
		return { claimable, claimed, remaining };
	}, [wallets]);

	const onClaimed = useCallback(() => {
		void refetch();
		void queryClient.invalidateQueries({ queryKey: ['pool-vesting-claims', address] });
		void queryClient.invalidateQueries({ queryKey: ['getBalance'] });
	}, [address, queryClient, refetch]);

	if (pkgLoading || isPending) {
		return (
			<div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-[#e2e8f0]">
				<p className="text-sm text-[#fcd34d]">正在读取线性释放 Wallet…</p>
			</div>
		);
	}

	if (!packageId) {
		return (
			<div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-[#e2e8f0]">
				<p className="text-sm font-medium text-[#fde68a]">线性释放（链上）</p>
				<p className="mt-1 text-xs leading-relaxed text-[#fcd34d]/90">
					未配置 vesting 包 ID（`VITE_CPC_VESTING_PACKAGE_ID` / `cpc-vesting-package-ids.json`），无法查询
					Wallet。请重新部署 Explorer 环境或完成 genesis vesting embed。
				</p>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-[#fecaca]">
				<p className="text-sm">读取 Vesting Wallet 失败</p>
			</div>
		);
	}

	if (wallets.length === 0) {
		return (
			<div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-[#e2e8f0]">
				<p className="text-sm font-medium text-[#fde68a]">线性释放 · {poolName}</p>
				<p className="mt-1 text-xs leading-relaxed text-[#fcd34d]/90">
					此地址尚无 `vesting::linear::Wallet`。可能仍是创世 Coin 形态，需先跑 `bootstrap-vesting`
					装入锁仓 Wallet。
				</p>
			</div>
		);
	}

	return (
		<div className="rounded-xl border border-amber-400/35 bg-amber-400/10 px-4 py-3 text-[#e2e8f0]">
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<div>
					<p className="text-xs font-medium uppercase tracking-wide text-[#fbbf24]">线性释放 · 10 年</p>
					<p className="mt-0.5 text-base font-semibold text-white">{poolName}</p>
				</div>
				<div className="text-right text-xs text-[#fcd34d]/90">
					<p>
						合计可领{' '}
						<span className="font-semibold tabular-nums text-white">
							{formatMist(totals.claimable)}
						</span>{' '}
						{CPC_SYMBOL}
					</p>
					<p className="mt-0.5">
						已领 {formatMist(totals.claimed)} · 锁定中 {formatMist(totals.remaining)}
					</p>
				</div>
			</div>
			<p className="mt-2 text-[11px] leading-relaxed text-[#94a3b8]">
				按时间连续解锁，无固定「几天一发」。连接池地址对应钱包后可领取当前可领额度到地址余额。
			</p>

			<div className="mt-3 space-y-3">
				{wallets.map((w) => (
					<WalletCard
						key={w.objectId}
						wallet={w}
						packageId={packageId}
						poolAddress={address}
						onClaimed={onClaimed}
					/>
				))}
			</div>

			<div className="mt-4 border-t border-white/10 pt-3">
				<p className="mb-2 text-xs font-medium text-[#cbd5e1]">最近领取记录</p>
				<ClaimHistory owner={address} packageId={packageId} />
			</div>
		</div>
	);
}
